// Which URLs belong to the /sign-in bundle, and what they carry. split.jsx imports this before any bundle loads.
//
// No imports on purpose: scripts/verify-sign-in.mjs runs this file in plain Node.

/**
 * The state v2/sign-in puts on the IdP round trip (core-backend SignInTokens.SSO_STATE_PREFIX). Customers registered
 * one redirect URI, /tenant-sso/{tenantId}, for the legacy login too; the legacy state is a 24-hex ObjectId, so the
 * prefix tells the two callbacks apart.
 */
export const SSO_STATE_PREFIX = 'ss_'

const param = (part, name) => new URLSearchParams((part ?? '').replace(/^[?#]/, '')).get(name)

export const isSignInPath = (pathname) => pathname === '/sign-in' || pathname.startsWith('/sign-in/')

/** Where Entra sends the browser back with Cubit's own app (core-backend SignInV2:Sso:RedirectUri): always ours. */
export const CUBIT_APP_SSO_CALLBACK = '/sign-in/sso'

/** An IdP callback that answers a v2/sign-in round trip; the legacy TenantSso handles every other one. */
export const isNewSsoCallback = ({ pathname, search, hash }) =>
    pathname.replace(/\/$/, '') === CUBIT_APP_SSO_CALLBACK ||
    (/^\/tenant-sso\/[^/]+\/?$/.test(pathname) &&
        (param(search, 'state') ?? param(hash, 'state') ?? '').startsWith(SSO_STATE_PREFIX))

export const isSignInEntry = (location) => isSignInPath(location.pathname) || isNewSsoCallback(location)

/** What the IdP sent back: the tenant from the path (none on Cubit's app's), the code and state, or its error. */
export const readSsoCallback = ({ pathname, search, hash }) => {
    const tenantId = pathname.startsWith('/tenant-sso/') ? (pathname.split('/')[2] ?? null) : null
    const read = (name) => param(search, name) ?? param(hash, name)
    return { tenantId, code: read('code'), state: read('state'), error: read('error') }
}

/**
 * `/sign-in?sso={tenantId}`: straight to the organisation's sign-in page, without the email step (the legacy SSO link
 * and "copy login link" lead here). Null for anything but a tenant id.
 */
export const readSsoEntry = ({ pathname, search }) => {
    const tenantId = pathname.replace(/\/$/, '') === '/sign-in' ? param(search, 'sso') : null
    return tenantId && /^[0-9a-f]{24}$/i.test(tenantId) ? { tenantId } : null
}

/** The reset token from `/sign-in/reset-password#token=rp_…` (a fragment never reaches a server). */
export const readResetToken = (hash) => param(hash, 'token')

/** Where to go after signing in: only a path on this site, and never back into /sign-in. */
export const safeReturnTo = (value) => {
    if (typeof value !== 'string' || !value.startsWith('/')) return null
    if (value.startsWith('//') || value.startsWith('/\\')) return null
    if (isSignInPath(value.split(/[?#]/)[0])) return null
    return value
}
